import { useState } from "react";
import { FiChevronDown, FiChevronUp, FiCpu, FiUsers, FiTag, FiTrendingUp } from "react-icons/fi";
import type { AIReviewSummary } from "../../../../types/clarification-review.type";

interface AISummaryPanelProps {
	summary: AIReviewSummary;
}

export const AISummaryPanel: React.FC<AISummaryPanelProps> = ({ summary }) => {
	const [isExpanded, setIsExpanded] = useState(true);

	const supplierEntries = Object.entries(summary.supplier_counts);

	return (
		<div className="rounded-lg border border-indigo-200 bg-indigo-50" data-testid="ai-summary-panel">
			<button
				type="button"
				className="flex w-full items-center justify-between px-4 py-3"
				onClick={() => setIsExpanded(!isExpanded)}
				data-testid="ai-summary-toggle"
			>
				<div className="flex items-center gap-2">
					<FiCpu className="text-indigo-600" size={16} />
					<span className="text-sm font-medium text-indigo-800">AI Review Summary</span>
				</div>
				{isExpanded ? (
					<FiChevronUp className="text-indigo-600" size={16} />
				) : (
					<FiChevronDown className="text-indigo-600" size={16} />
				)}
			</button>

			{isExpanded && (
				<div className="space-y-3 border-t border-indigo-200 px-4 pb-4 pt-3" data-testid="ai-summary-content">
					{/* Focus areas */}
					{summary.focus_areas.length > 0 && (
						<div>
							<div className="mb-1 flex items-center gap-1.5">
								<FiTag className="text-indigo-500" size={12} />
								<span className="text-xs font-medium text-indigo-700">Focus Areas</span>
							</div>
							<div className="flex flex-wrap gap-1.5">
								{summary.focus_areas.map((area) => (
									<span
										key={area}
										className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs text-indigo-700"
									>
										{area}
									</span>
								))}
							</div>
						</div>
					)}

					{/* Per-supplier counts */}
					{supplierEntries.length > 0 && (
						<div>
							<div className="mb-1 flex items-center gap-1.5">
								<FiUsers className="text-indigo-500" size={12} />
								<span className="text-xs font-medium text-indigo-700">Per Supplier</span>
							</div>
							<div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
								{supplierEntries.map(([supplier, count]) => (
									<div
										key={supplier}
										className="flex items-center justify-between rounded bg-white px-2 py-1 text-xs"
									>
										<span className="truncate text-gray-600">{supplier}</span>
										<span className="ml-1 font-medium text-indigo-700">{count}</span>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Themes */}
					{summary.themes.length > 0 && (
						<div>
							<div className="mb-1 flex items-center gap-1.5">
								<FiTrendingUp className="text-indigo-500" size={12} />
								<span className="text-xs font-medium text-indigo-700">Themes</span>
							</div>
							<ul className="list-inside list-disc space-y-0.5 text-xs text-gray-700">
								{summary.themes.map((theme) => (
									<li key={theme}>{theme}</li>
								))}
							</ul>
						</div>
					)}

					{/* Patterns */}
					{summary.patterns.length > 0 && (
						<div>
							<div className="mb-1 flex items-center gap-1.5">
								<FiCpu className="text-indigo-500" size={12} />
								<span className="text-xs font-medium text-indigo-700">Patterns Detected</span>
							</div>
							<ul className="list-inside list-disc space-y-0.5 text-xs text-gray-700">
								{summary.patterns.map((pattern) => (
									<li key={pattern}>{pattern}</li>
								))}
							</ul>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
